import { OakIconName } from "@oaknational/oak-components";
import { ZodType } from "zod";

import { ResourceType, ResourceTypeValueType } from "@/browser-lib/avo/Avo";

export type School = {
  urn: string;
  la: string;
  name: string;
  postcode: string;
  fullInfo: string;
  status: string;
};
export function parseSchoolToListItems(schools: School[]) {
  return schools.map((item) => {
    const comboItemKey = `${item.urn}-${item.name}`;
    const textValue = `${item.name}, ${item.la}, ${item.postcode}`;

    return {
      key: comboItemKey,
      textValue: String(textValue),
    };
  });
}

export function runSchema<T extends Record<string, unknown>>(
  schema: ZodType,
  data: T,
) {
  const rslt = schema.safeParse(data);
  const newErrors: Partial<Record<keyof T, string>> = {};
  if (!rslt.success) {
    for (const issue of rslt.error.issues) {
      const dataKey = String(issue.path[0]);
      newErrors[dataKey as keyof T] = issue.message;
    }
  }
  return {
    success: rslt.success,
    errors: newErrors,
  };
}

export type DownloadTypes =
  | "curriculumPlan"
  | "nationalCurriculum"
  | "curriculumQuality"
  | "whatsIncluded"
  | "assessment"
  | "commonQuestions"
  | "equipmentList";

const implementationGuidePdfBase = {
  group: "implementation-guide",
  icon: "subject-computing",
  fileExt: "PDF",
} as const;

export const DOWNLOAD_TYPE_LABELS: {
  id: DownloadTypes;
  label: string;
  group: string;
  disabled?: boolean;
  icon: OakIconName;
  subTitle?: string;
  fileExt: string;
  groupLabel?: string;
  avoResourceType: ResourceTypeValueType;
}[] = [
  {
    id: "curriculumPlan",
    group: "curriculum",
    label: "Curriculum plan and explainer",
    subTitle: "Word (accessible)",
    icon: "curriculum-plan",
    fileExt: "DOCX",
    avoResourceType: ResourceType.CURRICULUM_PLAN,
  },
  {
    id: "nationalCurriculum",
    group: "curriculum",
    label: "National curriculum alignment",
    subTitle: "Excel (accessible)",
    icon: "spreadsheet",
    fileExt: "XLSX",
    avoResourceType: ResourceType.CURRICULUM_DOCUMENT,
  },
  {
    id: "curriculumQuality",
    label: "Curriculum quality",
    groupLabel: "implementation guides",
    avoResourceType: ResourceType.CURRICULUM_QUALITY,
    ...implementationGuidePdfBase,
  },
  {
    id: "whatsIncluded",
    label: "What's included",
    groupLabel: "implementation guides",
    avoResourceType: ResourceType.WHATS_INCLUDED,
    ...implementationGuidePdfBase,
  },
  {
    id: "commonQuestions",
    label: "Frequently asked questions",
    groupLabel: "implementation guides",
    avoResourceType: ResourceType.COMMON_QUESTIONS,
    ...implementationGuidePdfBase,
  },
  {
    id: "equipmentList",
    label: "Equipment list",
    groupLabel: "implementation guides",
    avoResourceType: ResourceType.EQUIPMENT_LIST,
    ...implementationGuidePdfBase,
  },
  {
    id: "assessment",
    label: "Checking pupil understanding",
    groupLabel: "implementation guides",
    avoResourceType: ResourceType.ASSESSMENT,
    ...implementationGuidePdfBase,
  },
] as const;

export const DOWNLOAD_TYPES = DOWNLOAD_TYPE_LABELS.map(({ id }) => id);

export function assertValidDownloadType(val: string) {
  if (!DOWNLOAD_TYPES.includes(val as DownloadType)) {
    throw new Error(`Invalid type ${val}`);
  }
  return val as DownloadType;
}

export type DownloadType = (typeof DOWNLOAD_TYPES)[number];

export type DownloadTypeLabel = (typeof DOWNLOAD_TYPE_LABELS)[number];
